import Image from "next/image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/custom/ui/avatar";
import { Badge } from "@/components/ui/custom/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/custom/ui/tooltip";
import { Info } from "lucide-react";

export const columnDef = [
  {
    accessorKey: "rank",
    header: () => (
      <div className="flex items-center justify-center gap-2 p-2">
        <div className="text-xs whitespace-nowrap">RANK</div>
      </div>
    ),
    cell: ({ row }) => {
      const rank = row.original.rank
      return (
        <div className={`flex items-center justify-center gap-2 ${rank <= 5 ? 'p-2 -m-4' : ''}`}>
          {rank <= 5 && (
            <Image src={`/assets/rank-badges/rank_${rank}.png`} alt="rank" width={50} height={50} sizes="50px" />
          )}
          {rank > 5 && (
            <span className="gotham text-white font-bold text-center">{rank}</span>
          )}
        </div>
      )
    }
  },
  {
    accessorKey: "user",
    header: () => (
      <div className="flex items-center gap-2">
        <div className="text-xs whitespace-nowrap">USER</div>
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-start gap-2 text-left">
          <Avatar className="h-8 w-8 bg-metaxseed-blue">
            <AvatarImage src={`/placeholder.svg?height=32&width=32&text=${row.original.name.charAt(0)}`} />
            <AvatarFallback className="bg-metaxseed-blue text-white">{row.original.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-white">{row.original.name}</div>
            <div className="text-xs text-gray-400">{row.original.wallet.slice(0, 5).toUpperCase() + ' ... ' + row.original.wallet.slice(-5).toUpperCase()}</div>
          </div>
        </div>
      )
    }
  },
  {
    accessorKey: "mxs",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">MXS</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue/80 text-white">
              <p>MXS points are accumulated by playing games on the MXS platform.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-yellow-500 p-2 rounded-md w-[90px]">
          <img src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf" alt="xb" className="w-4 h-4" />
          <div className="gotham text-white font-bold">{row.original.mxs.toFixed(0)}</div>
        </div>
      )
    }
  },
  {
    accessorKey: "gold",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">GOLD</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>GOLD is accumulated by playing games on the MXS platform.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2 bg-gradient-to-r from-gray-500 to-gray-600 p-2 rounded-md w-[90px]">
          <img src="https://res.cloudinary.com/dot2onqdb/image/upload/v1729068489/gold_lr_h4yg2m.png" alt="gold" className="w-4 h-4" />
          <div className="gotham text-white font-bold">{row.original.gold.toFixed(0)}</div>
        </div>
      )
    }
  },
  {
    accessorKey: "ruby",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">RUBY</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>RUBY is accumulated by holding XSEED in your wallet.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center gap-2">
          <span>{row.original.ruby.toFixed(0)}</span>
        </div>
      )
    }
  },
  {
    accessorKey: "sapphire",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">SAPPHIRE</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>SAPPHIRE is accumulated by purchasing Node Licenses.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "level_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">LEVEL COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>LEVEL COUNT is the number of levels you have completed.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "missions_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">MISSIONS COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>MISSIONS COUNT is the number of missions you have completed.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "login_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">LOGIN COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>LOGIN COUNT is the number of times you have logged in to the MXS platform.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "ads_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">ADS COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>ADS COUNT is the number of ads you have watched.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "user_referral_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">USER REFERRAL COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>USER REFERRAL COUNT is the number of users you have referred.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "products_referral",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">PRODUCTS REFERRAL</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>PRODUCTS REFERRAL is the number of products you have referred.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "nft_referral_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">NFT REFERRAL COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>NFT REFERRAL COUNT is the number of nfts you have referred.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "nft_count",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">NFT COUNT</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>NFT COUNT is the number of NFTs you have in your wallet.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "nft_power",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">NFT POWER</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>NFT POWER is the power of your NFTs.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  },
  {
    accessorKey: "xb",
    header: () => (
      <div className="flex items-center justify-center gap-2">
        <div className="text-xs whitespace-nowrap">XSEED BALANCE</div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="bg-metaxseed-blue text-white">
              <p>XSEED BALANCE is the balance of XSEED in your wallet.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2 bg-gradient-to-r relative from-metaxseed-blue to-blue-500 p-2 rounded-md w-[140px]">
          <img src="https://res.cloudinary.com/dot2onqdb/image/upload/v1729067770/xseed_lr_is7mcx.png" alt="xb" className="w-4 h-4" />
          <div className="gotham text-white font-bold">{row.original.xb.toFixed(2)}</div>
        </div>
      )
    }
  }
]
